import clsx from 'clsx'
import { clamp } from 'lodash-es'
import Slider from '@mui/material/Slider'
import { styled } from '@mui/system'

import tailwind from '_/tailwind.config'

interface IRangeSliderProps {
  min: number
  max: number
  value: number[]
  enableInput: boolean
  setRange: (value: number[]) => void
  disabled?: boolean
}

const StyledRangeSlider = styled(Slider)({
  width: '100%',
  flexShrink: 1,
  '& .MuiSlider-rail': {
    borderRadius: 4
  },
  '&:not(.Mui-disabled) .MuiSlider-rail': {
    backgroundColor: tailwind.theme.colors.lighthouse['100']
  }
})

const RangeSlider = ({
  min,
  max,
  value,
  enableInput,
  setRange,
  disabled = false
}: IRangeSliderProps) => {
  const updateLowerValue = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newVal = Number(event.target.value)

    const clampedVal = clamp(value[1], newVal, max)

    setRange([newVal, clampedVal])
  }

  const updateHigherValue = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newVal = Number(event.target.value)

    setRange([value[0], newVal])
  }

  const updateValue = (_evt: Event, value: number | number[]): void => {
    const newVal = value as number[]

    setRange(newVal)
  }

  return (
    <div
      className={clsx('flex items-center gap-x-4', disabled && 'opacity-60')}
    >
      {enableInput && (
        <div className='relative flex w-28 items-center'>
          <input
            type='number'
            className={clsx('w-full pr-4', disabled && 'bg-gray-100')}
            value={value[0].toString()}
            onChange={updateLowerValue}
            min={min}
            max={max}
            disabled={disabled}
            data-testid='lower-bound-line-distance'
          />
          <span className='absolute right-2 text-gray-300 pointer-events-none'>
            ft
          </span>
        </div>
      )}
      <div className='flex flex-1'>
        <StyledRangeSlider
          value={value}
          onChange={updateValue}
          valueLabelDisplay='auto'
          aria-labelledby='range-slider'
          min={min}
          max={max}
          disabled={disabled}
        />
      </div>
      {enableInput && (
        <div className='relative flex w-28 items-center'>
          <input
            type='number'
            className={clsx('w-full pr-4', disabled && 'bg-gray-100')}
            value={value[1].toString()}
            onChange={updateHigherValue}
            min={min}
            max={max}
            disabled={disabled}
            data-testid='upper-bound-line-distance'
          />
          <span className='absolute right-2 text-gray-300 pointer-events-none'>
            ft
          </span>
        </div>
      )}
    </div>
  )
}

export default RangeSlider
